import { AuthState } from '@tritt/firebase-api'
import { AuthAdapter, MockTenant, createMockAuthState } from './auth-adapter.interface'

/**
 * Mock auth adapter for Cypress E2E tests.
 * 
 * Provides immediate auth state injection without network calls.
 * Exposes window hooks for Cypress to control auth and tenant state.
 */
export class MockAuthAdapter implements AuthAdapter {
    private authState: AuthState | null = null
    private listeners: Set<(user: AuthState | null) => void> = new Set()
    private tenants: MockTenant[] = []

    async init(): Promise<void> {
        // Check for initial auth state set by Cypress before page load
        // @ts-ignore
        if (window.cyInitialAuth) {
            // @ts-ignore
            this.authState = createMockAuthState(window.cyInitialAuth)
            console.log('[MockAuthAdapter] Using initial auth from window:', this.authState.email)
        }

        // Check for initial tenants set by Cypress before page load
        // @ts-ignore
        if (window.cyInitialTenants) {
            // @ts-ignore
            this.tenants = window.cyInitialTenants
            console.log('[MockAuthAdapter] Using initial tenants from window:', this.tenants.length)
        }

        // Expose Cypress hooks on window
        this.setupCypressHooks()

        console.log('[MockAuthAdapter] Initialized in mock mode')
    }

    private setupCypressHooks(): void {
        // @ts-ignore - Cypress hooks
        window.cySetAuth = (state: Partial<AuthState>) => {
            this.authState = createMockAuthState(state)
            this.notifyListeners()
            console.log('[MockAuthAdapter] Auth state set:', this.authState.email)
        }

        // @ts-ignore - Cypress hooks
        window.cyClearAuth = () => {
            this.authState = null
            this.notifyListeners()
            console.log('[MockAuthAdapter] Auth state cleared')
        }

        // @ts-ignore - Cypress hooks
        window.cySetTenants = (tenants: MockTenant[]) => {
            this.tenants = tenants
            console.log('[MockAuthAdapter] Tenants set:', tenants.length)
        }

        // @ts-ignore - Cypress hooks
        window.cyGetTenants = () => this.tenants

        // @ts-ignore - Cypress hooks  
        window.cyGetAuthState = () => this.authState
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async login(email: string, password: string): Promise<void> {
        // In mock mode, login just sets a default auth state
        this.authState = createMockAuthState({ email })
        this.notifyListeners()
        console.log('[MockAuthAdapter] Mock login:', email)
    }

    async logout(): Promise<void> {
        this.authState = null
        this.notifyListeners()
        console.log('[MockAuthAdapter] Mock logout')
    }

    getAuthState(): AuthState | null {
        return this.authState
    }

    async getAccessToken(): Promise<string | undefined> {
        return this.authState?.token
    }

    onAuthStateChange(callback: (user: AuthState | null) => void): () => void {
        this.listeners.add(callback)

        // Immediately call with current state
        callback(this.authState)

        return () => {
            this.listeners.delete(callback)
        }
    }

    private notifyListeners(): void {
        this.listeners.forEach(cb => cb(this.authState))
    }

    /**
     * Get mock tenants (for org.service integration).
     */
    getTenants(): MockTenant[] {
        return this.tenants
    }
}

// Declare window types for Cypress hooks
declare global {
    interface Window {
        useMockBackend?: boolean
        cySetAuth?: (state: Partial<AuthState>) => void
        cyClearAuth?: () => void
        cySetTenants?: (tenants: MockTenant[]) => void
        cyGetTenants?: () => MockTenant[]
        cyGetAuthState?: () => AuthState | null
    }
}
